import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { PipelineCardDetail as PipelineCardDetailDto } from '@/pages/pipelines/common/types';

import { PipelineDetailContent } from './content';
import { PipelineDetailErrorState, PipelineDetailLoadingState } from './states';
import { DetailBackdrop, DetailPopup } from './styled';

type Props = {
  open: boolean;
  detail?: PipelineCardDetailDto;
  loading: boolean;
  error: boolean;
  onClose: () => void;
  // Extension point for consumers that need to inject stage-specific action
  // forms (e.g. a fund-admin "Confirm funds received" / "Issue units" panel)
  // without forking the drawer. Undefined renders nothing extra.
  renderExtraContent?: (_detail: PipelineCardDetailDto) => ReactNode;
};

export const PipelineDetailDrawer = ({
  open,
  detail,
  loading,
  error,
  onClose,
  renderExtraContent,
}: Props) => {
  const navigate = useNavigate();

  const navigateTo = (href: string | null | undefined) => {
    if (href) navigate(href);
  };

  return (
    <BaseDialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <BaseDialog.Portal>
        <DetailBackdrop />
        <DetailPopup>
          {loading && <PipelineDetailLoadingState />}
          {error && <PipelineDetailErrorState />}
          {detail && !loading && !error && (
            <PipelineDetailContent
              detail={detail}
              onNavigate={navigateTo}
              extraContent={renderExtraContent?.(detail)}
            />
          )}
        </DetailPopup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
};
